import React, { useEffect, useState, useRef } from "react";
import { useForm } from 'react-hook-form';
import Swal from "sweetalert2";
import '../libs/css/bootstrap.css';
import '../libs/css/style.css';
import '../libs/css/responsive.css';
import '../libs/css/custom-bootstrap-margin-padding.css';
import $ from "jquery";
import Loaders from "./Dashboards/Loaders";
import { useNavigate } from "react-router-dom";
import { API_ROUTES } from '../utils/constants';

const RESEND_COOLDOWN_MS = 15 * 60 * 1000; // 15 minutes

const SPECIAL_CHAR_REGEX = /[!@#$%^&*(),.?":{}|<>_\-+=[\]\\/;'`~]/;

const formatCountdown = (ms) => {
    const totalSeconds = Math.max(0, Math.ceil(ms / 1000));
    const minutes = String(Math.floor(totalSeconds / 60)).padStart(2, '0');
    const seconds = String(totalSeconds % 60).padStart(2, '0');
    return `${minutes}:${seconds}`;
};

export default function Login(props){
    useEffect(() => {document.title = "Admin Login | Forthsoft"}, []);

    const { objectToFormData } = props;
    const rememberLogin = localStorage.getItem('rememberLoginFs');
    const rememberMe = rememberLogin ? true : false;
    const userInput = localStorage.getItem('userInputFs');
    const inputObject = JSON.parse(userInput);
    const isEmail = inputObject ? atob(inputObject.email) : '';
    const isPassword = inputObject ? atob(inputObject.password) : '';

    const {
        register: registerLogin,
        handleSubmit: handleLoginSubmit,
        formState: { errors: loginErrors, isSubmitting: loginSubmitting, isValid: loginValid }
    } = useForm({
        mode: "onChange",
        defaultValues: {
            email: rememberMe ? isEmail : '',
            password: rememberMe ? isPassword : '',
            remember: rememberMe
        }
    });

    const {
        register: registerForgot,
        handleSubmit: handleForgotSubmit,
        formState: { errors: forgotErrors, isSubmitting: forgotSubmitting }
    } = useForm({ mode: "onChange" });

    const {
        register: registerOtp,
        handleSubmit: handleOtpSubmit,
        formState: { errors: otpErrors, isSubmitting: otpSubmitting }
    } = useForm({ mode: "onChange" });

    const {
        register: registerReset,
        handleSubmit: handleResetSubmit,
        watch: watchReset,
        formState: { errors: resetErrors, isSubmitting: resetSubmitting }
    } = useForm({ mode: "onChange" });

    // step: 'login' | 'forgot' | 'otp' | 'reset'
    const [step, setStep] = useState('login');
    const [isChecked, setIsChecked] = useState(rememberMe);
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const navigate = useNavigate();

    const [resetEmail, setResetEmail] = useState('');
    const [resetToken, setResetToken] = useState('');
    const [resendAvailableAt, setResendAvailableAt] = useState(null);
    const [now, setNow] = useState(Date.now());
    const [resending, setResending] = useState(false);
    const tickRef = useRef(null);

    const userData = localStorage.getItem('userDataFs');
    const userObj = JSON.parse(userData);
    const isAuthenticated = { 'isLoggedIn': userObj !== null ? userObj.isLoggedIn : false }

    if(isAuthenticated.isLoggedIn){
        navigate("/dashboard");
    }

    useEffect(() => {
        if(step !== 'otp'){
            if(tickRef.current){
                clearInterval(tickRef.current);
                tickRef.current = null;
            }
            return;
        }
        tickRef.current = setInterval(() => setNow(Date.now()), 1000);
        return () => {
            if(tickRef.current){
                clearInterval(tickRef.current);
                tickRef.current = null;
            }
        };
    }, [step]);

    const millisTillResend = resendAvailableAt ? Math.max(0, resendAvailableAt - now) : 0;
    const canResend = millisTillResend <= 0;

    const goToLogin = () => {
        setStep('login');
        setResetEmail('');
        setResetToken('');
        setResendAvailableAt(null);
    }

    const forgotPassLink = (e) => {
        e && e.preventDefault();
        setStep('forgot');
    }

    const requestResetCode = async (data) => {
        try {
            const response = await fetch(API_ROUTES.ADMIN_FORGOT_PASSWORD, {
                method: "POST",
                body: objectToFormData({ email: data.email }),
            });
            const responseJSON = await response.json();

            if(responseJSON.status.toString() === "200"){
                setResetEmail(data.email);
                setResendAvailableAt(Date.now() + RESEND_COOLDOWN_MS);
                setStep('otp');
                Swal.fire({
                    title: 'Code Sent',
                    html: responseJSON.message || 'A verification code has been sent to your email.',
                    icon: 'success',
                    timer: 1500,
                    showConfirmButton: false,
                });
            }else{
                Swal.fire({
                    title: "Error",
                    html: responseJSON.message || 'Unable to send reset code.',
                    icon: "error"
                });
            }
        } catch (error) {
            Swal.fire({
                icon: "error",
                html: error.message || 'Something went wrong.',
                title: "Error",
            });
        }
    }

    const resendCode = async () => {
        if(!canResend || resending) return;
        setResending(true);
        try {
            const response = await fetch(API_ROUTES.ADMIN_RESEND_TOKEN, {
                method: "POST",
                body: objectToFormData({ email: resetEmail }),
            });
            const responseJSON = await response.json();

            if(responseJSON.status.toString() === "200"){
                setResendAvailableAt(Date.now() + RESEND_COOLDOWN_MS);
                Swal.fire({
                    title: 'Code Resent',
                    html: responseJSON.message || 'A new verification code has been sent to your email.',
                    icon: 'success',
                    timer: 1500,
                    showConfirmButton: false,
                });
            }else{
                Swal.fire({
                    title: "Error",
                    html: responseJSON.message || 'Unable to resend code.',
                    icon: "error"
                });
            }
        } catch (error) {
            Swal.fire({
                icon: "error",
                html: error.message || 'Something went wrong.',
                title: "Error",
            });
        } finally {
            setResending(false);
        }
    }

    const verifyCode = async (data) => {
        try {
            const response = await fetch(API_ROUTES.ADMIN_VERIFY_RESET_TOKEN, {
                method: "POST",
                body: objectToFormData({ email: resetEmail, reset_token: data.reset_token }),
            });
            const responseJSON = await response.json();

            if(responseJSON.status.toString() === "200"){
                setResetToken(data.reset_token);
                setStep('reset');
            }else{
                Swal.fire({
                    title: "Error",
                    html: responseJSON.message || 'Invalid or expired code.',
                    icon: "error"
                });
            }
        } catch (error) {
            Swal.fire({
                icon: "error",
                html: error.message || 'Something went wrong.',
                title: "Error",
            });
        }
    }

    const submitNewPassword = async (data) => {
        try {
            const response = await fetch(API_ROUTES.ADMIN_RESET_PASSWORD, {
                method: "POST",
                body: objectToFormData({
                    email: resetEmail,
                    reset_token: resetToken,
                    new_password: data.new_password,
                    new_password_confirmation: data.new_password_confirmation,
                }),
            });
            const responseJSON = await response.json();

            if(responseJSON.status.toString() === "200"){
                Swal.fire({
                    title: 'Success',
                    html: responseJSON.message || 'Your password has been reset successfully.',
                    icon: 'success',
                    timer: 1800,
                    showConfirmButton: false,
                });
                goToLogin();
                navigate("/login");
            }else{
                Swal.fire({
                    title: "Error",
                    html: responseJSON.message || 'Unable to reset password.',
                    icon: "error"
                });
            }
        } catch (error) {
            Swal.fire({
                icon: "error",
                html: error.message || 'Something went wrong.',
                title: "Error",
            });
        }
    }

    const togglePassword = () => {
        setShowPassword(!showPassword);
    }

    const toggleNewPassword = () => {
        setShowNewPassword(!showNewPassword);
    }


    const loginAuth = async (data) => {
        // console.log(data);
        // return;
        try {
            const response = await fetch(API_ROUTES.SIGN_IN, {
                method: "POST",
                body: objectToFormData(data),
            });
            const responseJSON = await response.json();
            const message = responseJSON.message;

            if(responseJSON.status.toString() === "200"){
                const responseDetails = responseJSON.data;

                const userData = {};
                userData.isLoggedIn = true;
                userData.responseDetails = responseDetails;

                global.tokens = userData?.responseDetails?.token;

                if(responseDetails.twoFa_status === 1){ // is2FA
                    const response1 = await fetch(API_ROUTES.TWO_FA_TOKEN, {
                        method: 'POST',
                        body: objectToFormData(data),
                        headers: {'token': userData?.responseDetails?.token},
                    });
                    const responseJSON2 = await response1.json();
                    // console.log(responseJSON2);

                    const result = await Swal.fire({
                        title: '2 Factor Authentication! Enter the code sent to your email and continue',
                        html: `<div class="text-left swal_inputs swal_inputs_pass">
                                <input type="number" id="ccode" class="swal2-input" placeholder="Enter code">
                            </div>`,
                        confirmButtonText: 'Continue',
                        icon: 'question',
                        iconHtml: '?',
                        focusConfirm: false,
                        showCancelButton: true,
                        cancelButtonColor: '#999',
                        preConfirm: () => {
                            const ccode = Swal.getPopup().querySelector('#ccode').value
                            if (!ccode) {
                            Swal.showValidationMessage(`Please enter code sent to your email`);
                            return;
                            }
                            return { ccode: ccode }
                        }
                    })
                    if (result.isConfirmed) {
                        Swal.fire({
                            title: 'Processing...',
                            text: "Please wait a second for a response...",
                            icon: 'success',
                            showConfirmButton: false,
                            confirmButtonColor: '#027937',
                            cancelButtonColor: '#d33',
                        });
                        try {
                            const dataString = {
                                twoFatoken: result.value.ccode,
                                email: userData?.responseDetails?.email,
                            };
                            const response = await fetch(API_ROUTES.TWO_FA_CHECK, {
                                method: 'POST',
                                body: objectToFormData(dataString),
                                headers: {'token': userData?.responseDetails?.token},
                            });
                            const responseJSON = await response.json();
                            if (responseJSON.status === 200) {
                                localStorage.setItem('userDataFs', JSON.stringify(userData));
                                const userInput = {};
                                userInput.email = btoa(data.email);
                                userInput.password = btoa(data.password);

                                if(isChecked){
                                    localStorage.setItem('userInputFs', JSON.stringify(userInput));
                                }else{
                                    localStorage.removeItem('userInputFs');
                                }
                                localStorage.setItem('rememberLoginFs', data.remember);

                                Swal.fire({
                                    title: 'Successful',
                                    html: "",
                                    icon: 'success',
                                    timer: 1000,
                                });
                                navigate("/dashboard");
                                return;
                            } else {
                                Swal.fire({
                                    title: 'Error',
                                    html: responseJSON.message || 'An error occurred during the POST request.',
                                    icon: 'error',
                                });
                                return;
                            }
                        } catch (error) {
                            Swal.fire({
                                title: 'Error',
                                text: error.message || 'An error occurred during the POST request.',
                                icon: 'error',
                            });
                            return;
                        }
                    };

                }else{
                    localStorage.setItem('userDataFs', JSON.stringify(userData));
                    const userInput = {};
                    userInput.email = btoa(data.email);
                    userInput.password = btoa(data.password);

                    if(isChecked){
                        localStorage.setItem('userInputFs', JSON.stringify(userInput))
                    }else{
                        localStorage.removeItem('userInputFs');
                    }
                    localStorage.setItem('rememberLoginFs', data.remember);
                    navigate("/dashboard");
                }


            }else{
                Swal.fire({
                    title: "<font className='error_title'>Error</font>",
                    html: `${message}`,
                    icon: "error"
                });
            }
        } catch (error) {
            Swal.fire({
                icon:   "error",
                html:   error,
                title:  "Error",
            });
        }
    }

    return (
        <div className="container-fluid_">

            <Loaders />

            <div className="row mt-xs--10">

                <div className="col-xl-6 d-xl-block d-none"><img className="bg-img-cover bg-center" src={require('../images/login.jpg')} alt="looginpage" /></div>

                <div className="col-xl-6 p-0">
                    <div className="login-card login-dark pl-xs-90 pr-xs-90">
                        <div>
                            <div className="text-center_">
                                <a className="logo text-center" href="">
                                    <img className="img-fluids for-light" src={require('../images/logo2-black.png')} alt="logo" />
                                    <span className="ml-10" style={{ fontSize:15, fontWeight:'600', color:'#fff' }}>Admin</span>
                                </a>
                            </div>
                            {
                                step === 'login' &&
                                <div className="login-main mt--10 mt-xs-20">
                                    <form className="theme-form" onSubmit={handleLoginSubmit(loginAuth)}>
                                        <h4>Sign in to Your Admin Account</h4>
                                        <p style={{ fontSize:'15px' }}>Enter your login credentials</p>
                                        <div className="form-group mt-xs-20">
                                            <label className="col-form-label">Email Address</label>
                                            {loginErrors.email && <label className="errors">{loginErrors.email?.message}</label>}

                                            <input className="form-control" type="email" name="email" required="" placeholder="Enter email or phone" {...registerLogin("email", {
                                                required: "Your email or phone is required"
                                            })} />
                                        </div>

                                        <div className="form-group mt-15">
                                            <label className="col-form-label">Password</label>
                                            {loginErrors.password && <label className="errors">{loginErrors.password?.message}</label>}

                                            <div className="form-input position-relative">
                                                <input
                                                className="form-control"
                                                type={showPassword ? 'text' : 'password'}
                                                name="password"
                                                placeholder="Enter password"
                                                defaultValue={password}
                                                onChange={(e) => setPassword(e.target.value)}
                                                {...registerLogin("password", {required: "Your password is required"})} />
                                                <div className="show-hide" onClick={togglePassword}>{showPassword ? 'hide' : 'show'}</div>
                                            </div>
                                        </div>

                                        <div className="form-group mt-15 mb-10">
                                            <div className="checkbox p-0">
                                                <input id="checkbox1" type="checkbox" name="remember" />
                                                    <label className="text-muted" htmlFor="checkbox1" {...registerLogin('remember')} checked={isChecked} onChange={() => setIsChecked(!isChecked)}>Remember password</label>
                                            </div>

                                            <a className="link" href="#" onClick={forgotPassLink}>Forgot password?</a>

                                            <div className="text-end mt-15">
                                                <button className="btn btn-primary btn-block w-100" type="submit" disabled={loginSubmitting || !loginValid} style={{ opacity: loginSubmitting ? '0.5' : 1 }}>{loginSubmitting ? 'Signing In...' : 'Sign in'}</button>
                                            </div>
                                        </div>
                                        {/* <p className="mt-4 mb-0 text-center">Don't have account?<a className="ms-2" href="sign-up.html">Create Account</a></p> */}
                                    </form>
                                </div>
                            }

                            {
                                step === 'forgot' &&
                                <div className="login-main mt--10 mt-xs-20">
                                    <form className="theme-form" onSubmit={handleForgotSubmit(requestResetCode)}>
                                        <h4>Reset Your Password</h4>
                                        <p style={{ fontSize:'15px' }}>Enter your email associated with Forthsoft</p>
                                        <div className="form-group mt-xs-20">
                                            <label className="col-form-label">Email Address</label>
                                            {forgotErrors.email && <label className="errors">{forgotErrors.email?.message}</label>}
                                            <input className="form-control" type="email" placeholder="Enter your email address" {...registerForgot("email", {
                                                required: "Your email is required",
                                                pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: "Enter a valid email address" }
                                            })} />
                                        </div>
                                        <div className="form-group mt-30 mb-10">
                                            <div className="text-end mt-15">
                                                <button className="btn btn-primary login_btn btn-block w-100" type="submit" disabled={forgotSubmitting} style={{ opacity: forgotSubmitting ? '0.5' : 1 }}>{forgotSubmitting ? 'Sending Code...' : 'Send Reset Code'}</button>
                                            </div>
                                        </div>
                                        <div className="text-center">
                                            <a href="#" onClick={(e) => { e.preventDefault(); goToLogin(); }}>Go back</a>
                                        </div>
                                    </form>
                                </div>
                            }

                            {
                                step === 'otp' &&
                                <div className="login-main mt--10 mt-xs-20">
                                    <form className="theme-form" onSubmit={handleOtpSubmit(verifyCode)}>
                                        <h4>Enter Verification Code</h4>
                                        <p style={{ fontSize:'15px' }}>Enter the code sent to <strong>{resetEmail}</strong></p>
                                        <div className="form-group mt-xs-20">
                                            <label className="col-form-label">Verification Code</label>
                                            {otpErrors.reset_token && <label className="errors">{otpErrors.reset_token?.message}</label>}
                                            <input className="form-control" type="text" inputMode="numeric" autoComplete="one-time-code" placeholder="Enter verification code" {...registerOtp("reset_token", {
                                                required: "Please enter the verification code"
                                            })} />
                                        </div>

                                        <div className="form-group mt-15 mb-10">
                                            <div className="text-muted" style={{ fontSize: '14px' }}>
                                                {
                                                    canResend
                                                        ? <a href="#" className="link" onClick={(e) => { e.preventDefault(); resendCode(); }}>{resending ? 'Resending...' : 'Resend code'}</a>
                                                        : <span>Resend code in {formatCountdown(millisTillResend)}</span>
                                                }
                                            </div>

                                            <div className="text-end mt-15">
                                                <button className="btn btn-primary btn-block w-100" type="submit" disabled={otpSubmitting} style={{ opacity: otpSubmitting ? '0.5' : 1 }}>{otpSubmitting ? 'Verifying...' : 'Verify Code'}</button>
                                            </div>
                                        </div>
                                        <div className="text-center">
                                            <a href="#" onClick={(e) => { e.preventDefault(); setStep('forgot'); }}>Go back</a>
                                        </div>
                                    </form>
                                </div>
                            }

                            {
                                step === 'reset' &&
                                <div className="login-main mt--10 mt-xs-20">
                                    <form className="theme-form" onSubmit={handleResetSubmit(submitNewPassword)}>
                                        <h4 >Set a New Password</h4>
                                        <p style={{ fontSize:'15px'}}>Your password must be at least 6 characters and include a capital letter, a number and a special character</p>

                                        <div className="form-group mt-xs-20">
                                            <label className="col-form-label mb-2 borde">New Password</label>
                                            {resetErrors.new_password && <label className="errors mt-4" >{resetErrors.new_password?.message}</label>}
                                            <div className="form-input position-relative">
                                                <input
                                                className="form-control mt-2"
                                                type={showNewPassword ? 'text' : 'password'}
                                                placeholder="Enter new password"
                                                {...registerReset("new_password", {
                                                    required: "Please enter a new password",
                                                    minLength: { value: 6, message: "Password must be at least 6 characters long" },
                                                    validate: {
                                                        hasUpper: (v) => /[A-Z]/.test(v) || "Password must contain at least one capital letter",
                                                        hasNumber: (v) => /[0-9]/.test(v) || "Password must contain at least one number",
                                                        hasSpecial: (v) => SPECIAL_CHAR_REGEX.test(v) || "Password must contain at least one special character",
                                                    }
                                                })} />
                                                <div className="show-hide" onClick={toggleNewPassword}>{showNewPassword ? 'hide' : 'show'}</div>
                                            </div>
                                        </div>

                                        <div className="form-group mt-15">
                                            <label className="col-form-label">Confirm New Password</label>
                                            {resetErrors.new_password_confirmation && <label className="errors">{resetErrors.new_password_confirmation?.message}</label>}
                                            <input
                                            className="form-control"
                                            type={showNewPassword ? 'text' : 'password'}
                                            placeholder="Re-enter new password"
                                            {...registerReset("new_password_confirmation", {
                                                required: "Please confirm your new password",
                                                validate: (v) => v === watchReset("new_password") || "Passwords do not match",
                                            })} />
                                        </div>

                                        <div className="form-group mt-30 mb-10">
                                            <div className="text-end mt-15">
                                                <button className="btn btn-primary btn-block w-100" type="submit" disabled={resetSubmitting} style={{ opacity: resetSubmitting ? '0.5' : 1 }}>{resetSubmitting ? 'Resetting...' : 'Reset Password'}</button>
                                            </div>
                                        </div>
                                    </form>
                                </div>
                            }
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
